import React from "react";
import { useTranslation } from "react-i18next";
import useResetScroll from "../../utils/useResetScroll";
import { FileText, Mail, ShieldAlert } from "lucide-react";

export default function TermsAndConditions() {
  useResetScroll();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl shadow-indigo-900/10 overflow-hidden border border-slate-100 relative">
        <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
        
        <div className="pt-8 pb-6 px-6 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-indigo-50 rounded-xl mb-3">
            <FileText className="w-6 h-6 text-indigo-600" />
          </div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900 mb-1">
            {t("Terms & Conditions")}
          </h1>
          <p className="text-slate-500 text-sm font-medium">Ota Life</p>
        </div>

        <div className="px-6 pb-6 space-y-6 text-slate-700 text-sm leading-relaxed">
          <p className="text-slate-600 text-xs italic">
            {t("By accessing or using Ota Life, you agree to these Terms & Conditions.")}
          </p>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              1. {t("Account & Access")}
            </h2>
            <p>{t("You must provide accurate information. You are responsible for maintaining account security.")}</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              2. {t("App Services")}
            </h2>
            <p className="mb-2">{t("Ota Life provides:")}</p>
            <ul className="space-y-1 ml-4">
              <li>• {t("Event & workshop ticket booking")}</li>
              <li>• {t("Anime & Japanese culture content")}</li>
              <li>• {t("Quizzes & competitions")}</li>
              <li>• {t("User-submitted ideas & community voting")}</li>
              <li>• {t("Reels and curated media")}</li>
            </ul>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              3. {t("Tickets & Payments")}
            </h2>
            <ul className="space-y-1 ml-4">
              <li>• {t("All purchases are final unless stated otherwise")}</li>
              <li>• {t("Event changes or cancellations will be notified through the app")}</li>
              <li>• {t("Ota Life is not responsible for third-party payment issues")}</li>
            </ul>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              4. {t("User Content & Submissions")}
            </h2>
            <p className="mb-2">{t("When you submit content (ideas, images, forms), you grant Ota Life a license to use, display, and distribute that content within the platform.")}</p>
            <p className="font-medium">{t("You must not submit illegal, offensive, or copyrighted material.")}</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              5. {t("Community & Conduct")}
            </h2>
            <p className="mb-2">{t("You agree not to:")}</p>
            <ul className="space-y-1 ml-4 mb-3">
              <li>• {t("Harass other users")}</li>
              <li>• {t("Post harmful or illegal content")}</li>
              <li>• {t("Abuse app features or systems")}</li>
            </ul>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex gap-2">
                <ShieldAlert className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 font-medium">
                  {t("Violation may result in suspension or permanent ban.")}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              6. {t("Intellectual Property")}
            </h2>
            <p>{t("All app content, branding, logos, and materials belong to Ota Life or its partners.")}</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              7. {t("Liability Disclaimer")}
            </h2>
            <p className="mb-2">{t("Ota Life is not liable for:")}</p>
            <ul className="space-y-1 ml-4">
              <li>• {t("Event cancellations or venue changes")}</li>
              <li>• {t("User-submitted content accuracy")}</li>
              <li>• {t("Technical issues beyond reasonable control")}</li>
            </ul>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              8. {t("Termination")}
            </h2>
            <p>{t("We may suspend or terminate your account for violation of these terms.")}</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              9. {t("Governing Law")}
            </h2>
            <p>{t("These terms are governed by the laws of India.")}</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              10. {t("Contact")}
            </h2>
            <p className="mb-2">{t("For any concerns:")}</p>
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
              <a 
                href="mailto:hiro.ganji@be-native.life"
                className="flex items-center gap-2 text-indigo-800 font-medium hover:text-indigo-900 transition-colors"
              >
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs">hiro.ganji@be-native.life</span>
              </a>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 p-4 text-center border-t border-slate-100">
          <p className="text-xs text-slate-600 mb-2">{t("Questions About These Terms?")}</p>
          <a
            href="mailto:hiro.ganji@be-native.life"
            className="inline-flex items-center gap-2 text-indigo-600 text-sm font-semibold hover:text-indigo-700 transition-colors"
          >
            <Mail className="w-4 h-4" />
            hiro.ganji@be-native.life
          </a>
        </div>
      </div>
    </div>
  );
}
